/**
 * Correct an auto-classified merchant category (Task 21).
 * Writes merchant_category_observations, updates the transaction + merchant defaults,
 * and outbox entries. Future purchases use ≥2 consistent observations (docs/03 §2)
 * and updated merchant defaults immediately.
 */

import type { CardZappDB } from '../db/database';
import { enqueueOutbox } from '../db/outbox';
import { payloadHash } from '../db/canonicalize';
import type { BusinessDate } from '../domain/time';
import { isAutoClassifiedCategory } from '../domain/recommend/categoryObservations';
import type { SpendCategory, TxnChannel } from '../domain/recommend/types';
import type {
  MerchantCategoryObservationLocal,
  MerchantLocal,
  TransactionLocal,
} from '../schemas/entities';

export type CorrectMerchantCategoryInput = {
  db: CardZappDB;
  transaction_id: string;
  corrected_category: SpendCategory;
  observed_at: BusinessDate;
  now_iso: string;
};

export type CorrectMerchantCategoryResult = {
  observation_id: string;
  merchant_id: string;
};

function stripLocal(row: object): Record<string, unknown> {
  const { _localDirty: _d, _baseServerUpdatedAt: _b, ...rest } = row as Record<string, unknown>;
  void _d;
  void _b;
  return rest;
}

function applyMerchantCategory(
  merchant: MerchantLocal,
  channel: TxnChannel,
  category: SpendCategory,
  now: string,
): MerchantLocal {
  const next: MerchantLocal = {
    ...merchant,
    default_category: category,
    category_confidence: 'user_reported',
    last_verified_at: now.slice(0, 10),
    updated_at: now,
    _localDirty: true,
  };
  if (channel === 'online') next.online_category = category;
  if (channel === 'in_store') next.in_store_category = category;
  return next;
}

async function loadTransaction(db: CardZappDB, id: string): Promise<TransactionLocal> {
  const row = await db.transactions.where('id').equals(id).first();
  if (row === undefined || row.deleted_at !== null) {
    throw new Error('transaction_not_found');
  }
  return row;
}

async function loadMerchant(db: CardZappDB, id: string): Promise<MerchantLocal> {
  const row = await db.merchants.where('id').equals(id).first();
  if (row === undefined || row.deleted_at !== null) {
    throw new Error('merchant_not_found');
  }
  return row;
}

/**
 * Persist a user category correction for an auto-classified purchase.
 * Rejects user-sourced categories and transactions without a merchant_id.
 */
export async function correctMerchantCategory(
  input: CorrectMerchantCategoryInput,
): Promise<CorrectMerchantCategoryResult> {
  const txn = await loadTransaction(input.db, input.transaction_id);
  if (txn.merchant_id === null) {
    throw new Error('merchant_required');
  }
  const categorySrc = txn.category_src;
  if (!isAutoClassifiedCategory(categorySrc)) {
    throw new Error('not_auto_classified');
  }

  const merchant = await loadMerchant(input.db, txn.merchant_id);

  const observationId = crypto.randomUUID();
  const now = input.now_iso;
  const merchantId = txn.merchant_id;
  const channel = txn.channel;
  const predicted = txn.category;

  const observation: MerchantCategoryObservationLocal = {
    id: observationId,
    user_id: null,
    merchant_id: merchantId,
    card_id: txn.card_id,
    transaction_id: txn.id,
    channel,
    predicted_category: predicted,
    predicted_rate_bps: null,
    observed_reward_cents: null,
    implied_rate_bps: null,
    implied_category: input.corrected_category,
    observed_at: input.observed_at,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const updatedTxn: TransactionLocal = {
    ...txn,
    category: input.corrected_category,
    category_src: 'user',
    updated_at: now,
    _localDirty: true,
  };

  const updatedMerchant = applyMerchantCategory(merchant, channel, input.corrected_category, now);

  const obsPayload = stripLocal(observation);
  const txnPayload = stripLocal(updatedTxn);
  const merchantPayload = stripLocal(updatedMerchant);
  const obsHash = await payloadHash(obsPayload);
  const txnHash = await payloadHash(txnPayload);
  const merchantHash = await payloadHash(merchantPayload);

  const baseTxn = txn._baseServerUpdatedAt;
  const baseMerchant = merchant._baseServerUpdatedAt;

  await input.db.transaction(
    'rw',
    [
      input.db.merchant_category_observations,
      input.db.transactions,
      input.db.merchants,
      input.db.outbox,
    ],
    async () => {
      await input.db.merchant_category_observations.put(observation);
      await enqueueOutbox(input.db, {
        table: 'merchant_category_observations',
        op: 'insert',
        rowId: observationId,
        payload: obsPayload,
        payloadHash: obsHash,
        baseServerUpdatedAt: null,
        createdAt: now,
      });

      await input.db.transactions.put(updatedTxn);
      await enqueueOutbox(input.db, {
        table: 'transactions',
        op: 'update',
        rowId: txn.id,
        payload: txnPayload,
        payloadHash: txnHash,
        baseServerUpdatedAt: baseTxn,
        createdAt: now,
      });

      await input.db.merchants.put(updatedMerchant);
      await enqueueOutbox(input.db, {
        table: 'merchants',
        op: 'update',
        rowId: merchant.id,
        payload: merchantPayload,
        payloadHash: merchantHash,
        baseServerUpdatedAt: baseMerchant,
        createdAt: now,
      });
    },
  );

  return { observation_id: observationId, merchant_id: merchantId };
}
